import { Component, inject, ChangeDetectionStrategy, effect, signal, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, map } from 'rxjs/operators';
import { RisrList } from './risr-list';
import { RisrFormTransformer } from '../forms/risr-form-transformer';
import { RisrForm, RisrFormFieldControl } from '../forms/risr-form';
import { RisrFormRenderer } from '../forms/risr-form-renderer';
import { RisrFormFieldRenderer } from '../forms/risr-form-field-renderer';
import { RisrExpandableSchema } from '../forms/models/risr-schema-fields';

@Component({
    selector: 'risr-list-filter',
    imports: [
        MatButtonModule,
        MatIconModule,
        MatSelectModule,
        MatTooltipModule,
        RisrFormRenderer,
        RisrFormFieldRenderer,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [`
        :host {
            /* TODO: Perhaps would be better if the risr-list-filter would be part of the page header */
            --surface: var(--color-white, #fff);
            --padding: calc(var(--base-padding, 0.64em) * 2);
            --space: var(--space-x, 0.64em);
            --radius: var(--border-radius, 4px);
            --card-shadow: var(--box-shadow, 0 2px 4px rgba(0, 0, 0, 0.1));

            background-color: var(--surface);
            border-radius: var(--radius);
            box-shadow: var(--card-shadow);
            display: block;
            margin: var(--space) 0;
            padding: var(--padding);
        }

        .featured-row {
            display: flex;
            align-items: center;
            gap: 12px;
        }
        .featured-row risr-form-field-renderer {
            flex: 1;
            min-width: 0;
        }
        .sort-picker {
            flex-shrink: 0;
            width: 200px;
        }
        .advanced-toggle {
            flex-shrink: 0;
        }
        .advanced-container {
            border: 1px solid rgba(0, 0, 0, 0.12);
            border-radius: 4px;
            padding: 16px;
            margin-top: 8px;
        }
        .advanced-actions {
            display: flex;
            justify-content: flex-end;
            gap: 8px;
            margin-top: 8px;
        }
    `],
    template: `
        @if (featuredFields().length > 0 || advancedForm() || availableSorts().length > 0) {
            <div class="featured-row">
                @for (field of featuredFields(); track field.name) {
                    <risr-form-field-renderer [field]="field"></risr-form-field-renderer>
                }
                @if (availableSorts().length > 0) {
                    <mat-form-field class="sort-picker" appearance="outline">
                        <mat-label i18n>Sort by</mat-label>
                        <mat-select [value]="selectedSortId()" (selectionChange)="onSortChange($event.value)">
                            @for (sort of availableSorts(); track sort.id) {
                                <mat-option [value]="sort.id">{{ sort.name }}</mat-option>
                            }
                        </mat-select>
                    </mat-form-field>
                }
                @if (advancedForm()) {
                    <button matButton="outlined" class="advanced-toggle" aria-controls="advanced-filters" aria-expanded="{{ showAdvanced() }}" (click)="toggleAdvanced()">
                        <mat-icon fontIcon="{{ showAdvanced() ? 'filter_list_off' : 'tune' }}"></mat-icon>
                        Filters
                    </button>
                }
            </div>
        }
        @if (showAdvanced() && advancedForm()) {
            <div class="advanced-container" id="advanced-filters">
                <risr-form-renderer [risrForm]="advancedForm()!"></risr-form-renderer>
                <div class="advanced-actions">
                    <button mat-stroked-button (click)="resetFilters()" i18n>
                        <mat-icon>clear</mat-icon> Reset
                    </button>
                    <button mat-flat-button (click)="applyFilters()" i18n>
                        <mat-icon>search</mat-icon> Search
                    </button>
                </div>
            </div>
        }
    `
})
export class RisrListFilter implements OnDestroy {
    private readonly list = inject<RisrList<any>>(RisrList);
    private readonly transformer = inject(RisrFormTransformer);
    private readonly destroy$ = new Subject<void>();
    private readonly debouncedValue$ = new Subject<Record<string, any>>();

    protected readonly filterForm = signal<RisrForm | null>(null);
    protected readonly advancedForm = signal<RisrForm | null>(null);
    protected readonly showAdvanced = signal(false);

    protected readonly featuredFields = signal<RisrFormFieldControl[]>([]);
    protected readonly availableSorts = this.list.availableSorts;
    protected readonly selectedSortId = signal<string | null>(null);

    constructor() {
        this.debouncedValue$.pipe(
            debounceTime(400),
            map(value => this.stripEmpty(value)),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            takeUntil(this.destroy$)
        ).subscribe(value => {
            this.list.updateFilter(value);
        });

        effect(() => {
            const sorts = this.availableSorts();
            if (sorts.length === 0) return;

            const currentSort = this.list.resource.sort();
            if (currentSort?.sort_on) {
                const match = sorts.find(s =>
                    s.sort.sort_on === currentSort.sort_on && s.sort.sort_order === currentSort.sort_order
                );
                if (match) {
                    this.selectedSortId.set(match.id);
                    return;
                }
            }

            const first = sorts[0];
            this.selectedSortId.set(first.id);
            this.list.updateSort(first.sort);
        });

        effect(async () => {
            const fields = this.list.availableFilters();
            if (fields.featured.length === 0 && fields.advanced.length === 0) return;

            const currentForm = this.filterForm();
            if (currentForm) return;

            const initialFilter = this.list.resource.filter() ?? {};

            if (fields.featured.length > 0) {
                const schema: RisrExpandableSchema = { fields: fields.featured };
                const form = await this.transformer.fromExpandableSchema(schema, initialFilter);
                this.filterForm.set(form);

                const fieldControls = form.risrGroup.risrElements
                    .filter((el): el is RisrFormFieldControl => el.type === 'field');
                this.featuredFields.set(fieldControls);

                form.$value.pipe(
                    takeUntil(this.destroy$)
                ).subscribe(() => {
                    this.emitMergedValue();
                });
            }

            if (fields.advanced.length > 0) {
                const advancedSchema: RisrExpandableSchema = { fields: fields.advanced };
                const advForm = await this.transformer.fromExpandableSchema(advancedSchema, initialFilter);
                this.advancedForm.set(advForm);

                const hasAdvancedValues = fields.advanced.some((f: any) => {
                    const key = f.id || f.name;
                    const val = initialFilter[key];
                    return val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0);
                });
                if (hasAdvancedValues) {
                    this.showAdvanced.set(true);
                }

                advForm.$value.pipe(
                    takeUntil(this.destroy$)
                ).subscribe(() => {
                    this.emitMergedValue();
                });
            }
        });
    }

    private stripEmpty(value: Record<string, any>): Record<string, any> {
        return Object.entries(value).reduce((acc, [key, val]) => {
            if (val === null || val === undefined || val === '') return acc;
            if (Array.isArray(val) && val.length === 0) return acc;
            acc[key] = val;
            return acc;
        }, {} as Record<string, any>);
    }

    private emitMergedValue() {
        const featured = this.filterForm()?.value() ?? {};
        const advanced = this.showAdvanced() ? (this.advancedForm()?.value() ?? {}) : {};
        this.debouncedValue$.next({ ...featured, ...advanced });
    }

    onSortChange(sortId: string | null) {
        this.selectedSortId.set(sortId);
        const sort = this.availableSorts().find(s => s.id === sortId);
        this.list.updateSort(sort?.sort ?? {});
    }

    toggleAdvanced() {
        this.showAdvanced.update(v => !v);
        this.emitMergedValue();
    }

    applyFilters() {
        const featured = this.filterForm()?.value() ?? {};
        const advanced = this.showAdvanced() ? (this.advancedForm()?.value() ?? {}) : {};
        this.list.updateFilter({ ...featured, ...advanced });
    }

    resetFilters() {
        this.advancedForm()?.form.reset();
        this.filterForm()?.form.reset();
        this.filterForm()?.form.setValue({});
        this.emitMergedValue();
    }

    ngOnDestroy() {
        this.filterForm()?.destroy();
        this.advancedForm()?.destroy();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
